import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppLayoutProviders, SidebarLayout } from '../layout/SidebarLayout';
import { notesService } from '../adapters/notes';
import { SOAPNoteDetail } from '../types';
import toast from '../lib/toast';
import FloatingNoteChat from '../components/patient/FloatingNoteChat';
import Button from '../../components/ui/Button';
import Modal, { ModalBody, ModalFooter, ModalHeader } from '../../components/ui/Modal';
import SimpleLoader from '../../components/ui/SimpleLoader';
import { FormModelOption, FormCreate, viewModelPdf } from '../../services/apiService';
import { useAuth } from '../../context/authContext';
import { useResourceUsage } from '../../context/ResourceUsageContext';

const getPlainText = (html: string): string => {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
};

const Section = ({
  title,
  html,
  onCopy,
}: {
  title: string;
  html: string;
  onCopy: (label: string, text: string) => void;
}) => (
  <section className='border border-brand-navy/15 rounded-md p-4 bg-white shadow-sm'>
    <div className='flex items-center justify-between mb-2'>
      <h3 className='text-sm font-semibold text-brand-navy'>{title}</h3>
      <button
        onClick={() => onCopy(title, getPlainText(html))}
        disabled={!html}
        className='text-xs px-2 py-0.5 rounded border border-brand-navy/15 bg-white hover:bg-brand-navy/5 focus:outline-none focus:ring-2 focus:ring-brand-accent disabled:opacity-40 disabled:cursor-not-allowed'
      >
        Copy
      </button>
    </div>
    <div
      className='text-sm text-gray-800 min-h-[1.5rem] prose prose-sm max-w-none prose-p:my-2 prose-ul:my-2 prose-li:my-1'
      dangerouslySetInnerHTML={{ __html: html || '—' }}
    />
  </section>
);

const PatientNoteDetailInner = () => {
  const { id, noteId } = useParams<{ id: string; noteId: string }>();
  const navigate = useNavigate();
  const { onLogout } = useAuth();
  const { setUsage } = useResourceUsage();

  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState<SOAPNoteDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Forms state — same as SoapDetails.tsx
  const [noteFormData, setNoteFormData] = useState<any[]>([]);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const { call: formCreateView, loading: formCreateViewLoading } = FormModelOption();
  const { call: formCreate, loading: formCreateLoading } = FormCreate();
  const { call: formViewPdf, loading: formViewPdfLoading } = viewModelPdf();
  const [loadingFormIndex, setLoadingFormIndex] = useState<number | null>(null);
  const [activeFormId, setActiveFormId] = useState<string>('');
  const [isUpdateModalOpen, setUpdateModalOpen] = useState(false);
  const [showFormsDrawer, setShowFormsDrawer] = useState(false);
  const [modalData, setModalData] = useState<{
    s3_bucket?: string;
    s3_key?: string;
    formId?: string;
  } | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewFormData, setPreviewFormData] = useState<{
    formId: string;
    formName: string;
  } | null>(null);

  const fetchNote = useCallback(async () => {
    if (!noteId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await notesService.getNoteDetail(noteId);
      setNote(data);
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 404) {
        setError('Note not found');
      } else {
        setError(e?.response?.data?.msg || 'Failed to load note');
      }
    } finally {
      setLoading(false);
    }
  }, [noteId]);

  useEffect(() => { fetchNote(); }, [fetchNote]);

  // Load forms on mount — same as SoapDetails
  useEffect(() => {
    if (noteId) loadFormData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteId]);

  const loadFormData = () => {
    if (!noteId) return;
    setIsFormLoading(true);
    formCreateView({ noteId }).subscribe({
      next(res: any) {
        setNoteFormData(res?.data || []);
        setIsFormLoading(false);
      },
      error(err: any) {
        setIsFormLoading(false);
        if (err?.status === 409) onLogout(err?.status);
        console.error(err);
      },
    });
  };

  const toggleFormsDrawer = () => {
    setShowFormsDrawer(!showFormsDrawer);
    if (!showFormsDrawer && noteId) loadFormData();
  };

  const handleCreateForm = (formId: string, formName: string) => {
    setActiveFormId(formId);
    formCreate({ noteId, formId, formUrl: 'https://example.com/form' }).subscribe({
      next(res: any) {
        toast.success(`${formName} created successfully.`);
        loadFormData();
        setUsage((prev: any) => ({
          ...prev,
          formsCount: res?.data?.formsCount || prev.formsCount,
        }));
      },
      error(e: any) {
        if (e?.status === 409) onLogout(e?.status);
        toast.error(e?.data?.msg || e.message);
      },
    });
  };

  const handleViewForm = (
    formUrl: any,
    formId: string,
    index: number,
    forceCreate: boolean = false,
    needToUseOld: boolean = false,
  ) => {
    setLoadingFormIndex(index);
    setActiveFormId(formId);
    formUrl = JSON.parse(formUrl);
    formUrl.formId = formId;
    formUrl.forceCreate = forceCreate;
    formUrl.noteId = noteId;
    setModalData(formUrl);
    formViewPdf(JSON.stringify(formUrl)).subscribe({
      next: (res: any) => {
        if (res?.data?.updated && !needToUseOld) {
          setUpdateModalOpen(true);
        } else {
          const byteArray = res?.data?.pdf?.data;
          const blob = new Blob([new Uint8Array(byteArray)], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);
          setUpdateModalOpen(false);
          window.open(url, '_blank');
          setLoadingFormIndex(null);
          setTimeout(() => URL.revokeObjectURL(url), 5000);
        }
      },
      error: (error: any) => {
        setLoadingFormIndex(null);
        if (error?.status === 409) onLogout(error?.status);
        console.error('Error opening PDF:', error);
      },
    });
  };

  const handlePreviewForm = (formId: string, formName: string) => {
    setPreviewFormData({ formId, formName });
    setShowPreviewModal(true);
  };

  const copyText = (label: string, text: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text)
      .then(() => toast.success(`${label} copied`))
      .catch(() => toast.error('Copy failed'));
  };

  const copyAll = () => {
    if (!note) return;
    const all = [
      `Consent:\n${getPlainText(note.consent)}`,
      `Subjective:\n${getPlainText(note.subjective)}`,
      `Objective:\n${getPlainText(note.objective)}`,
      `Assessment:\n${getPlainText(note.assessment)}`,
      `Treatment:\n${getPlainText(note.treatment)}`,
      `Plan:\n${getPlainText(note.plan)}`,
    ].join('\n\n');
    navigator.clipboard.writeText(all)
      .then(() => toast.success('All sections copied'))
      .catch(() => toast.error('Copy failed'));
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center h-48 text-sm text-gray-500'>
        Loading note…
      </div>
    );
  }

  if (error || !note) {
    return (
      <div>
        <div className='flex items-center justify-between mb-4'>
          <h1 className='text-lg font-semibold text-gray-800'>SOAP Note</h1>
          <button
            onClick={() => navigate(`/patients/${id}`)}
            className='text-xs px-3 py-1.5 rounded border border-gray-300 bg-white hover:bg-gray-50'
          >
            Back
          </button>
        </div>
        <div className='text-sm text-red-500'>{error ?? 'Note not found'}</div>
      </div>
    );
  }

  const createdDisplay = note.created_at ? new Date(note.created_at).toLocaleString() : '—';
  const typeRaw = (note.note_type || '').toUpperCase();
  const isInitial = typeRaw === 'INITIAL';
  const typeLabel = isInitial ? 'Initial Note' : typeRaw === 'FOLLOWUP' ? 'Follow-up Note' : 'Note';
  const badgeClasses = isInitial
    ? 'bg-brand-navy/10 text-brand-navy border-brand-navy/20'
    : 'bg-brand-accent/10 text-brand-accent border-brand-accent/20';

  return (
    <div className='min-h-screen pb-20'>
      {/* Main content */}
      <div className='mb-6'>
        <div className='flex items-start justify-between gap-4 flex-wrap'>
          <div>
            <h1 className='text-xl font-semibold text-brand-navy leading-tight'>SOAP Note</h1>
            <div className='mt-1 flex items-center gap-3 text-xs text-gray-600'>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full border font-medium tracking-wide uppercase text-[10px] ${badgeClasses}`}>
                {typeLabel}
              </span>
              <span className='h-3 w-px bg-gray-300' />
              <span>{createdDisplay}</span>
            </div>
          </div>
          <div className='flex gap-2 self-start'>
            <button
              onClick={() => navigate(`/patients/${id}`)}
              className='text-xs px-3 py-1.5 rounded border border-brand-navy/15 bg-white hover:bg-brand-navy/5 focus:outline-none focus:ring-2 focus:ring-brand-accent'
            >
              Back to Patient
            </button>
            <button
              onClick={copyAll}
              className='text-xs px-3 py-1.5 rounded border border-brand-navy/15 bg-white hover:bg-brand-navy/5 focus:outline-none focus:ring-2 focus:ring-brand-accent'
            >
              Copy All
            </button>
          </div>
        </div>
        <hr className='mt-4 border-brand-navy/15' />
      </div>

      <div className='grid gap-4 mb-8'>
        <Section title='Consent' html={note.consent} onCopy={copyText} />
        <Section title='Subjective' html={note.subjective} onCopy={copyText} />
        <Section title='Objective' html={note.objective} onCopy={copyText} />
        <Section title='Assessment' html={note.assessment} onCopy={copyText} />
        <Section title='Treatment' html={note.treatment} onCopy={copyText} />
        <Section title='Plan' html={note.plan} onCopy={copyText} />
      </div>

      {/* AI Chat floating button */}
      <FloatingNoteChat noteId={note.session_id} />

      {/* Forms toggle button — same position as SoapDetails */}
      {!showFormsDrawer && (
        <div className='fixed bottom-4 right-20 z-30'>
          <Button
            variant='solid'
            colorIntensity='600'
            className='flex primary-bg items-center justify-center w-16 h-16 text-sm rounded-full p-3 shadow-xl bg-blue-600 hover:bg-blue-700'
            onClick={toggleFormsDrawer}
          >
            <svg xmlns='http://www.w3.org/2000/svg' className='h-8 w-8 text-white' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' />
            </svg>
            <span className='sr-only'>Forms</span>
            {noteFormData && noteFormData.length > 0 && (
              <span className='absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-xs text-white'>
                {noteFormData.filter((form) => form.isCreated).length}
              </span>
            )}
          </Button>
        </div>
      )}

      {/* Forms Drawer — exact copy from SoapDetails */}
      <div
        className={`fixed right-0 top-[80px] z-30 h-[calc(100%-70px)] w-[400px] bg-white shadow-xl transition-transform duration-300 ease-in-out dark:bg-slate-800 ${
          showFormsDrawer ? 'translate-x-0' : 'translate-x-full'
        } md:w-[500px]`}
      >
        <div className='flex h-full flex-col'>
          <div className='flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-4 dark:border-gray-700 dark:bg-slate-700'>
            <h2 className='flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white'>
              <svg xmlns='http://www.w3.org/2000/svg' className='h-5 w-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' />
              </svg>
              Forms for this note
            </h2>
            <button onClick={toggleFormsDrawer} className='text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'>
              <svg xmlns='http://www.w3.org/2000/svg' className='h-5 w-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
              </svg>
            </button>
          </div>

          <div className='flex-1 overflow-y-auto p-4'>
            {isFormLoading ? (
              <div className='flex justify-center py-8'>
                <SimpleLoader />
              </div>
            ) : noteFormData && noteFormData.length > 0 ? (
              <div className='divide-y divide-gray-200 dark:divide-gray-700'>
                {noteFormData.map((form, idx) => (
                  <div key={form.formId} className='py-4'>
                    <div className='flex flex-col gap-3'>
                      <div>
                        <h3 className='text-lg font-medium'>{form.formName}</h3>
                        {form.isCreated && (
                          <div className='mt-1 text-sm text-green-600 flex items-center dark:text-green-400'>
                            <svg className='inline-block h-4 w-4 mr-1' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
                            </svg>
                            Generated
                          </div>
                        )}
                      </div>
                      <div>
                        {form?.isCreated ? (
                          <Button
                            variant='solid'
                            colorIntensity='500'
                            className='primary-bg text-white w-full'
                            isLoading={loadingFormIndex === idx && activeFormId === form.formId}
                            onClick={() => handleViewForm(form.formUrl, form.formId, idx)}
                          >
                            View Form
                          </Button>
                        ) : (
                          <div className='flex gap-2'>
                            <Button
                              variant='solid'
                              colorIntensity='500'
                              className='primary-bg text-white flex-1'
                              isLoading={formCreateLoading && activeFormId === form.formId}
                              onClick={() => handleCreateForm(form.formId, form.formName)}
                            >
                              Generate
                            </Button>
                            <Button
                              variant='outline'
                              color='blue'
                              colorIntensity='500'
                              className='flex-1'
                              onClick={() => handlePreviewForm(form.formId, form.formName)}
                            >
                              Preview
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className='py-8 text-center text-gray-500'>
                <svg className='mx-auto h-12 w-12 text-gray-400 mb-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' />
                </svg>
                <p className='text-lg font-medium'>No forms available for this note</p>
                <p className='mt-1'>Contact administrator if you believe this is an error</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Update Form Modal — exact copy from SoapDetails */}
      <Modal
        isOpen={isUpdateModalOpen}
        setIsOpen={setUpdateModalOpen}
        isCentered
        size='sm'
        closeOnOverlayClick={false}
        data={modalData}
      >
        <ModalHeader setIsOpen={setUpdateModalOpen}>Update Form</ModalHeader>
        <ModalBody className='pt-3'>
          <p className='mb-0 text-lg'>Do you want to regenerate form?</p>
        </ModalBody>
        <ModalFooter className='justify-end pt-3'>
          <Button
            variant='solid'
            color='gray'
            colorIntensity='500'
            className='cursor-pointer text-white'
            isLoading={formViewPdfLoading}
            onClick={() =>
              handleViewForm(
                JSON.stringify({ s3_bucket: modalData?.s3_bucket, s3_key: modalData?.s3_key }),
                modalData?.formId ?? '',
                1,
                false,
                true,
              )
            }
          >
            No
          </Button>
          <Button
            variant='solid'
            colorIntensity='600'
            className='primary-bg cursor-pointer text-white'
            isLoading={formViewPdfLoading}
            onClick={() =>
              handleViewForm(
                JSON.stringify({ s3_bucket: modalData?.s3_bucket, s3_key: modalData?.s3_key }),
                modalData?.formId ?? '',
                1,
                true,
                false,
              )
            }
          >
            Yes
          </Button>
        </ModalFooter>
      </Modal>

      {/* Form Preview Modal — exact copy from SoapDetails */}
      <Modal isOpen={showPreviewModal} setIsOpen={setShowPreviewModal} isCentered size='md'>
        <ModalHeader setIsOpen={setShowPreviewModal}>Form Preview: {previewFormData?.formName}</ModalHeader>
        <ModalBody className='p-4'>
          <div className='flex flex-col items-center'>
            <div className='mb-4 rounded border border-gray-200 p-2 shadow-sm'>
              <div className='bg-gray-50 p-4 rounded'>
                <div className='mx-auto w-full max-w-md'>
                  <div className='mb-4 border-b-2 border-gray-800 pb-2'>
                    <h3 className='text-xl font-bold text-center'>{previewFormData?.formName}</h3>
                    <p className='text-center text-sm text-gray-500'>Sample Form Layout</p>
                  </div>
                  <div className='mb-4 space-y-2'>
                    <div className='h-6 bg-gray-200 rounded w-full'></div>
                    <div className='h-6 bg-gray-200 rounded w-3/4'></div>
                    <div className='h-6 bg-gray-200 rounded w-1/2'></div>
                  </div>
                  <div className='mb-4 p-2 border border-gray-300 rounded'>
                    <div className='h-20 bg-gray-100 rounded'></div>
                  </div>
                  <div className='grid grid-cols-2 gap-3'>
                    <div className='h-8 bg-gray-200 rounded'></div>
                    <div className='h-8 bg-gray-200 rounded'></div>
                  </div>
                </div>
              </div>
            </div>
            <p className='text-sm text-gray-500 mb-4 text-center'>
              This is a preview of the form layout. The actual form will include patient data from this note.
            </p>
            <div className='flex gap-3'>
              <Button variant='outline' color='gray' colorIntensity='500' onClick={() => setShowPreviewModal(false)}>
                Close
              </Button>
              <Button
                variant='solid'
                colorIntensity='500'
                className='primary-bg text-white'
                onClick={() => {
                  setShowPreviewModal(false);
                  if (previewFormData) handleCreateForm(previewFormData.formId, previewFormData.formName);
                }}
              >
                Generate This Form
              </Button>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

const PatientNoteDetail = () => (
  <AppLayoutProviders>
    <SidebarLayout>
      <PatientNoteDetailInner />
    </SidebarLayout>
  </AppLayoutProviders>
);

export default PatientNoteDetail;
